/**
 * PhotoAnnotator — shared mark-up canvas (iter198pp, Feb 2026)
 * ==============================================================
 * Single source of truth for arrow/circle annotation across the app:
 *   • Tester MCC photo capture (MCCCardStack, MOTRapidCaptureCard)
 *   • FOH review of tester evidence in the MOT pricing left panel
 *   • Any other photo-capture entrypoint that wants to surface markup
 *
 * Why one component?
 *   Previously there were TWO implementations (shared/PhotoAnnotator.js
 *   red-only, and mot/PhotoAnnotator.jsx multi-colour). iter198pp
 *   merges them so we have one annotator with the better feature set:
 *   colour palette, full-screen modal, image-space coordinates so the
 *   markup lines up at any zoom, touch + mouse, sub-6px taps discarded.
 *
 * Props (back-compatible with the original red-only signature):
 *   photo        — string. Source image URL or data URL. (Required)
 *   sourceDataUrl — DEPRECATED alias for `photo`. Still accepted so
 *                   older callers don't break during the rename.
 *   onSave(dataUrl, meta)  — Called with the flattened JPEG data URL +
 *                            metadata `{ annotations_count }`.
 *   onCancel()  — Closes the annotator without uploading.
 */
import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Circle, Undo2, Trash2, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const COLOURS = [
  { id: "red",    hex: "#ef4444", label: "Red" },
  { id: "yellow", hex: "#facc15", label: "Yellow" },
  { id: "white",  hex: "#ffffff", label: "White" },
];

const TOOLS = [
  { id: "arrow",  icon: ArrowUpRight, label: "Arrow"  },
  { id: "circle", icon: Circle,       label: "Circle" },
];

const MIN_DRAG_PX   = 6;   // sub-6px drags discarded as accidental taps

// iter211g — Scale stroke + arrowhead with the image so the markup
// stays visible at any resolution. Previously hard-coded to 5 px /
// 22 px which became invisible on phone-camera JPEGs (e.g. 3000×4000
// → 5px is ~0.13% of width, barely a pencil line). New behaviour:
//   strokeWidth = 0.8% of min(width, height), floored at 8 px.
//   arrowHead   = 4× strokeWidth so it stays in proportion.
function _strokeWidthForImage(width, height) {
  const minDim = Math.max(1, Math.min(width || 0, height || 0));
  return Math.max(8, Math.round(minDim * 0.008));
}

function paintAnnotation(ctx, ann, canvasWidth, canvasHeight) {
  const strokePx    = _strokeWidthForImage(canvasWidth, canvasHeight);
  const arrowHeadPx = strokePx * 4;
  ctx.save();
  ctx.lineWidth   = strokePx;
  ctx.strokeStyle = ann.colour;
  ctx.fillStyle   = ann.colour;
  ctx.lineCap     = "round";
  ctx.lineJoin    = "round";

  if (ann.tool === "circle") {
    const cx = (ann.x1 + ann.x2) / 2;
    const cy = (ann.y1 + ann.y2) / 2;
    const rx = Math.abs(ann.x2 - ann.x1) / 2;
    const ry = Math.abs(ann.y2 - ann.y1) / 2;
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    ctx.stroke();
  } else {
    // arrow
    const dx  = ann.x2 - ann.x1;
    const dy  = ann.y2 - ann.y1;
    const len = Math.hypot(dx, dy) || 1;
    ctx.beginPath();
    ctx.moveTo(ann.x1, ann.y1);
    ctx.lineTo(ann.x2, ann.y2);
    ctx.stroke();
    const ang     = Math.atan2(dy, dx);
    const headLen = Math.min(arrowHeadPx, len * 0.4);
    const a1 = ang - Math.PI / 6;
    const a2 = ang + Math.PI / 6;
    ctx.beginPath();
    ctx.moveTo(ann.x2, ann.y2);
    ctx.lineTo(ann.x2 - headLen * Math.cos(a1), ann.y2 - headLen * Math.sin(a1));
    ctx.lineTo(ann.x2 - headLen * Math.cos(a2), ann.y2 - headLen * Math.sin(a2));
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}

function flattenToDataUrl(img, annotations) {
  const off = document.createElement("canvas");
  off.width  = img.naturalWidth;
  off.height = img.naturalHeight;
  const ctx = off.getContext("2d");
  ctx.drawImage(img, 0, 0);
  for (const ann of annotations) paintAnnotation(ctx, ann, off.width, off.height);
  return off.toDataURL("image/jpeg", 0.92);
}

const PhotoAnnotator = ({ photo, sourceDataUrl, onSave, onCancel }) => {
  // Accept either prop name during the rename window.
  const src = photo || sourceDataUrl;

  const canvasRef = useRef(null);
  const imageRef  = useRef(null);

  const [tool,        setTool]        = useState("arrow");
  const [colour,      setColour]      = useState(COLOURS[0].hex);
  const [annotations, setAnnotations] = useState([]);
  const [drag,        setDrag]        = useState(null);
  const [ready,       setReady]       = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [loadError,   setLoadError]   = useState(false);

  // Image load. crossOrigin allows toDataURL on remote photos when the
  // server emits a CORS header. Without it, canvases on remote http
  // photos taint and toDataURL throws — pre-empting that here.
  useEffect(() => {
    if (!src) return;
    const img = new Image();
    if (!src.startsWith("data:")) img.crossOrigin = "anonymous";
    img.onload  = () => { imageRef.current = img; setReady(true); };
    img.onerror = () => { setLoadError(true); };
    img.src = src;
  }, [src]);

  // Paint loop.
  useEffect(() => {
    if (!ready) return;
    const cv  = canvasRef.current;
    const img = imageRef.current;
    if (!cv || !img) return;
    cv.width  = img.naturalWidth;
    cv.height = img.naturalHeight;
    const ctx = cv.getContext("2d");
    ctx.clearRect(0, 0, cv.width, cv.height);
    ctx.drawImage(img, 0, 0);
    for (const ann of annotations) paintAnnotation(ctx, ann, cv.width, cv.height);
    if (drag) paintAnnotation(ctx, { ...drag, tool, colour }, cv.width, cv.height);
  }, [ready, annotations, drag, tool, colour]);

  // Screen → image-space coordinate map.
  const toImageCoords = (e) => {
    const cv = canvasRef.current;
    if (!cv) return { x: 0, y: 0 };
    const rect    = cv.getBoundingClientRect();
    const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    return {
      x: (clientX - rect.left) * (cv.width  / rect.width),
      y: (clientY - rect.top)  * (cv.height / rect.height),
    };
  };

  const handlePointerDown = (e) => {
    e.preventDefault();
    const { x, y } = toImageCoords(e);
    setDrag({ x1: x, y1: y, x2: x, y2: y });
  };
  const handlePointerMove = (e) => {
    if (!drag) return;
    e.preventDefault();
    const { x, y } = toImageCoords(e);
    setDrag((d) => (d ? { ...d, x2: x, y2: y } : d));
  };
  const handlePointerUp = () => {
    if (!drag) return;
    const len = Math.hypot(drag.x2 - drag.x1, drag.y2 - drag.y1);
    if (len > MIN_DRAG_PX) {
      setAnnotations((a) => [...a, { ...drag, tool, colour }]);
    }
    setDrag(null);
  };

  const handleUndo  = () => setAnnotations((a) => a.slice(0, -1));
  const handleClear = () => setAnnotations([]);

  const handleSave = () => {
    if (!imageRef.current) return;
    setSaving(true);
    // Defer one frame so the spinner has a chance to render on slow
    // devices before the synchronous toDataURL call.
    requestAnimationFrame(() => {
      let out;
      try {
        out = annotations.length
          ? flattenToDataUrl(imageRef.current, annotations)
          : src;
      } catch (err) {
        // CORS-tainted canvas → toDataURL throws. Falls back to the
        // unmodified source so we don't lose the user's intent
        // entirely; the annotations were drawn but cannot be exported.
        out = src;
      }
      setSaving(false);
      onSave?.(out, { annotations_count: annotations.length });
    });
  };

  if (!src) return null;

  return (
    <div
      className="fixed inset-0 z-[120] bg-slate-900/95 flex flex-col"
      data-testid="photo-annotator"
    >
      {/* ---------- Toolbar ---------- */}
      <div className="flex items-center justify-between p-3 bg-slate-800 border-b border-slate-700 flex-wrap gap-2">
        <div className="flex items-center gap-1">
          {TOOLS.map((t) => {
            const Icon = t.icon;
            const active = tool === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTool(t.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-bold uppercase tracking-wide transition-colors ${
                  active
                    ? "bg-teal-500 text-white"
                    : "bg-slate-700 text-slate-200 hover:bg-slate-600"
                }`}
                data-testid={`annotator-tool-${t.id}`}
                aria-pressed={active}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-1.5" data-testid="annotator-colours">
          {COLOURS.map((c) => {
            const active = c.hex === colour;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setColour(c.hex)}
                style={{ backgroundColor: c.hex }}
                className={`w-8 h-8 rounded-full border-2 transition-all ${
                  active ? "border-white scale-110" : "border-slate-500"
                }`}
                aria-label={c.label}
                data-testid={`annotator-colour-${c.id}`}
              />
            );
          })}
        </div>

        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={handleUndo}
            disabled={!annotations.length}
            className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-bold uppercase tracking-wide bg-slate-700 text-slate-200 hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="annotator-undo"
          >
            <Undo2 className="w-4 h-4" />
            Undo
          </button>
          <button
            type="button"
            onClick={handleClear}
            disabled={!annotations.length}
            className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-bold uppercase tracking-wide bg-slate-700 text-slate-200 hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="annotator-clear"
          >
            <Trash2 className="w-4 h-4" />
            Clear
          </button>
        </div>
      </div>

      {/* ---------- Canvas ---------- */}
      <div className="flex-1 overflow-hidden flex items-center justify-center p-2 select-none touch-none">
        {loadError ? (
          <p className="text-slate-300 text-sm" data-testid="annotator-load-error">
            Photo could not be loaded.
          </p>
        ) : ready ? (
          <canvas
            ref={canvasRef}
            className="max-w-full max-h-full rounded-lg shadow-xl bg-slate-800 cursor-crosshair"
            style={{ touchAction: "none" }}
            onMouseDown={handlePointerDown}
            onMouseMove={handlePointerMove}
            onMouseUp={handlePointerUp}
            onMouseLeave={handlePointerUp}
            onTouchStart={handlePointerDown}
            onTouchMove={handlePointerMove}
            onTouchEnd={handlePointerUp}
            data-testid="annotator-canvas"
          />
        ) : (
          <p className="text-slate-300 text-sm">Loading photo…</p>
        )}
      </div>

      {/* ---------- Footer ---------- */}
      <div className="flex items-center justify-between p-3 bg-slate-800 border-t border-slate-700">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="text-slate-100 border-slate-500 bg-slate-700 hover:bg-slate-600"
          data-testid="annotator-cancel"
        >
          <X className="w-4 h-4 mr-1.5" />
          Cancel
        </Button>
        <p className="text-xs text-slate-400 font-medium hidden sm:block">
          Tap-and-drag on the photo to {tool === "arrow" ? "draw an arrow" : "draw a circle"}.
        </p>
        <Button
          type="button"
          onClick={handleSave}
          disabled={saving || !ready}
          className="bg-teal-500 hover:bg-teal-400 text-white"
          data-testid="annotator-save"
        >
          <Check className="w-4 h-4 mr-1.5" />
          {saving ? "Saving…" : annotations.length ? `Save (${annotations.length})` : "Save"}
        </Button>
      </div>
    </div>
  );
};

export default PhotoAnnotator;
